import React from "react";
import { MetricResult, MetricsService } from "gen/clients";
import { ExportButton } from "components/ExportButton/ExportButton";
import { default as dayjs } from "dayjs";
import { secondsToMinutes } from "utils/dateUtils";

interface Props {
  clientId: string;
  from: Date;
  to: Date;
}

interface ExportedDataRow {
  username: string;
  requestCount: number;
  charactersCharged: number;
  textCharactersCharged: number;
  documentCharactersCharged: number;
  transcription: number;
  transcriptionAndTranslation: number;
  translationAndDubbing: number;
  translationAndSubtitling: number;
  [key: string]: string | number;
}

const columnHeaders = {
  username: "Username",
  requestCount: "Request Count",
  charactersCharged: "Characters Charged",
  textCharactersCharged: "Text Translation (characters)",
  documentCharactersCharged: "Document Translation (characters)",
  transcription: "Transcription (minutes)",
  transcriptionAndTranslation: "Transcription And Translation (minutes)",
  translationAndDubbing: "Translation And Dubbing (minutes)",
  translationAndSubtitling: "Translation And Subtitling (minutes)"
};

const ExportAnalyticsButton: React.FC<Props> = ({ clientId, from, to }) => {
  const getExportData = React.useCallback(async (): Promise<ExportedDataRow[]> => {
    const response = await MetricsService.getRequestCounts({
      clientId,
      startTime: from.toISOString(),
      endTime: to.toISOString()
    });

    return (response.results || [])
      .sort((r1, r2) => r2.values[0].reqCount - r1.values[0].reqCount)
      .map((row: MetricResult) => ({
        username: row.label,
        requestCount: row.values[0].reqCount,
        charactersCharged:
          (row.values[0].charactersCharged?.AZURE || 0) + (row.values[0].charactersCharged?.INTENTO || 0),
        textCharactersCharged: row.values[0].charactersChargedByUserRequest?.TEXT_TRANSLATION || 0,
        documentCharactersCharged: row.values[0].charactersChargedByUserRequest?.FILES_TRANSLATION || 0,
        transcription: secondsToMinutes(row.values[0].secondsChargedByUserRequest?.MEDIA_TRANSCRIBE || 0),
        transcriptionAndTranslation: secondsToMinutes(row.values[0].secondsChargedByUserRequest?.MEDIA_TRANSLATE || 0),
        translationAndDubbing: secondsToMinutes(row.values[0].secondsChargedByUserRequest?.MEDIA_DUBBING || 0),
        translationAndSubtitling: secondsToMinutes(
          row.values[0].secondsChargedByUserRequest?.MEDIA_SUBTITLE_EMBEDDING || 0
        )
      }));
  }, [clientId, from, to]);

  const fileName = `mt-requests-by-client-contact-${dayjs(from).format("MM-DD-YYYY")}-${dayjs(to).format("MM-DD-YYYY")}.csv`;

  return (
    <ExportButton<ExportedDataRow>
      filename={fileName}
      columnHeaders={columnHeaders}
      getExportData={getExportData}
      exportingMessage="Exporting records. This may take a moment..."
    />
  );
};

export { ExportAnalyticsButton };
